import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { Link, useNavigate } from "react-router-dom";
import { Check, CalendarPlus, Share2, Radio } from "lucide-react";
import { toast } from "sonner";
import WinCardModal from "@/components/WinCardModal";

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return ""; }
};

export default function Recaps() {
  const { user } = useAuth();
  const [recaps, setRecaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [win, setWin] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!user?.is_premium) { setLoading(false); return; }
    api.get("/coach/recaps").then(r => setRecaps(r.data.recaps || [])).catch(()=>{}).finally(() => setLoading(false));
  }, [user?.is_premium]);

  const toggleDone = async (r) => {
    const next = !r.done;
    setRecaps((rs) => rs.map((x) => (x.id === r.id ? { ...x, done: next } : x)));
    try { await api.post(`/coach/recaps/${r.id}/done`, { done: next }); }
    catch { setRecaps((rs) => rs.map((x) => (x.id === r.id ? { ...x, done: !next } : x))); toast.error("Try again"); }
  };

  if (!user?.is_premium) {
    return (
      <div className="max-w-md mx-auto px-5 pt-16 text-center">
        <div className="eyebrow text-[#FD0F10]">VOICE CHAT RECAPS</div>
        <h1 className="font-display text-3xl font-black mt-2">This is a Pro feature.</h1>
        <p className="text-gray-400 mt-3">Talk to your coach hands-free and every chat gets saved here with your next step.</p>
        <Link to="/pro" className="mt-6 inline-block hero-accent text-white font-bold px-6 py-3 rounded" data-testid="recaps-upgrade">Unlock with Pro Escape Pass</Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-5 pt-6" data-testid="recaps-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="eyebrow text-[#FD0F10]">FROM YOUR VOICE CHATS</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black mt-1">Every recap. One place.</h1>
        </div>
        <Link to="/coach" className="shrink-0 flex items-center gap-2 hero-accent text-white text-xs font-mono-ui uppercase tracking-wider px-4 py-2.5 rounded" data-testid="recaps-talk">
          <Radio size={14} /> Talk
        </Link>
      </div>

      {loading ? (
        <div className="mt-8 text-gray-400">Loading your recaps…</div>
      ) : recaps.length === 0 ? (
        <div className="mt-8 card-panel p-8 text-center">
          <div className="font-display text-xl font-bold">No recaps yet.</div>
          <p className="text-gray-400 mt-2">Have a quick hands-free chat with your coach and it will show up here.</p>
          <Link to="/coach" className="inline-block mt-4 hero-accent px-5 py-3 rounded font-bold" data-testid="recaps-empty-talk">Talk to your coach</Link>
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          {recaps.map((r) => (
            <div key={r.id} className="card-panel p-4" data-testid={`recap-item-${r.id}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="font-display font-bold text-white text-lg leading-snug">{r.title}</div>
                <span className="shrink-0 text-xs text-gray-500 font-mono-ui">{fmtDate(r.created_at)}</span>
              </div>
              <p className="text-sm text-gray-300 leading-relaxed mt-1">{r.summary}</p>
              <div className="mt-2 flex items-start gap-2">
                <span className="eyebrow text-[#FD0F10] mt-0.5 shrink-0">NEXT</span>
                <p className={`text-sm font-semibold leading-snug ${r.done ? "text-gray-500 line-through" : "text-white"}`}>{r.next_step}</p>
              </div>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <button onClick={() => toggleDone(r)} className={`flex items-center gap-1.5 text-xs font-mono-ui uppercase tracking-wider px-2.5 py-1.5 rounded border ${r.done ? "border-[#FD0F10] bg-[#FD0F10]/10 text-[#FD0F10]" : "border-[#2A2E39] text-gray-300 hover:border-[#FD0F10]"}`} data-testid={`recap-item-done-${r.id}`}>
                  <Check size={13} /> {r.done ? "Done" : "Mark done"}
                </button>
                <button onClick={() => nav("/checkin", { state: { focus: r.next_step } })} className="flex items-center gap-1.5 text-xs font-mono-ui uppercase tracking-wider px-2.5 py-1.5 rounded border border-[#2A2E39] text-gray-300 hover:border-[#FD0F10]" data-testid={`recap-item-checkin-${r.id}`}>
                  <CalendarPlus size={13} /> Check-in
                </button>
                {r.done && (
                  <button onClick={() => setWin(r)} className="flex items-center gap-1.5 text-xs font-mono-ui uppercase tracking-wider px-2.5 py-1.5 rounded border border-[#FD0F10] text-[#FD0F10] hover:bg-[#FD0F10] hover:text-white" data-testid={`recap-item-share-${r.id}`}>
                    <Share2 size={13} /> Share win
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <WinCardModal recap={win} onClose={() => setWin(null)} />
    </div>
  );
}
